/**
 * Generic, combinable multi-axis filtering engine, shared by every provider
 * feed (Radar, Jira, GitHub, Slack, Agents). An axis is either `single`
 * (exactly one selected value, or none) or `multi` (any number of selected
 * options, OR'd together). Selections across axes are AND'd. This is the
 * single place predicate/option logic for a feed's filters is written —
 * both option counts and list filtering read from it.
 */

export interface AxisOption<T> {
  value: string;
  label: string;
  predicate: (item: T) => boolean;
}

export interface FilterAxis<T> {
  id: string;
  label: string;
  type: 'single' | 'multi';
  options: AxisOption<T>[];
}

export type AxisFilterState = Record<string, string | string[]>;

export function defaultAxisFilterState<T>(axes: readonly FilterAxis<T>[]): AxisFilterState {
  const state: AxisFilterState = {};
  for (const axis of axes) {
    state[axis.id] = axis.type === 'multi' ? [] : '';
  }
  return state;
}

function axisHasSelection<T>(axis: FilterAxis<T>, value: string | string[] | undefined): boolean {
  if (axis.type === 'multi') return Array.isArray(value) && value.length > 0;
  return typeof value === 'string' && value !== '';
}

export function axisFiltersActive<T>(
  axes: readonly FilterAxis<T>[],
  state: AxisFilterState,
): boolean {
  return axes.some((axis) => axisHasSelection(axis, state[axis.id]));
}

function matchesAxis<T>(
  item: T,
  axis: FilterAxis<T>,
  value: string | string[] | undefined,
): boolean {
  if (!axisHasSelection(axis, value)) return true;
  const selected = axis.type === 'multi' ? (value as string[]) : [value as string];
  return axis.options.some((option) => selected.includes(option.value) && option.predicate(item));
}

export function matchesAxisFilters<T>(
  item: T,
  axes: readonly FilterAxis<T>[],
  state: AxisFilterState,
): boolean {
  return axes.every((axis) => matchesAxis(item, axis, state[axis.id]));
}

export function filterByAxes<T>(
  items: readonly T[],
  axes: readonly FilterAxis<T>[],
  state: AxisFilterState,
): T[] {
  return items.filter((item) => matchesAxisFilters(item, axes, state));
}

export function countAxisOption<T>(items: readonly T[], option: AxisOption<T>): number {
  return items.filter(option.predicate).length;
}
